import { Routes } from '@angular/router';
import { authGuard, guestGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';
import { VENDOR_PORTAL_ROLES } from './core/models/user.model';

import { PlaceholderComponent } from './features/simplified/placeholder/placeholder.component';
import { DashboardShellComponent } from './features/simplified/dashboard_shell/dashboard-shell.component';
import { FilesShellComponent } from './features/simplified/files_shell/files-shell.component';
import { OrdersShellComponent } from './features/simplified/orders_shell/orders-shell.component';
import { VendorPortalShellComponent } from './features/simplified/vendor_portal_shell/vendor-portal-shell.component';
import { CustomerServiceShellComponent } from './features/simplified/customer_service_shell/customer-service-shell.component';
import { ReportsShellComponent } from './features/simplified/reports_shell/reports-shell.component';
import { AdminShellComponent } from './features/simplified/admin_shell/admin-shell.component';

export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
  {
    path: 'authentication',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/pages/login.page').then((m) => m.LoginPage),
  },
  // Legacy /login → /authentication (bookmark preservation)
  { path: 'login', redirectTo: 'authentication', pathMatch: 'full' },
  {
    // Password reset (spec §3.5) — guest routes; the reset link is emailed to
    // logged-out users. reset-password reads ?token=&email= from the query string.
    path: 'authentication/forgot-password',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/pages/forgot-password.page').then((m) => m.ForgotPasswordPage),
  },
  {
    path: 'authentication/reset-password',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/pages/reset-password.page').then((m) => m.ResetPasswordPage),
  },
  {
    // Authenticated app — Daxa layout shell wraps every feature route.
    path: '',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./layouts/main-layout/main-layout.component').then((m) => m.MainLayoutComponent),
    children: [
      // Dashboard — the real, role-adaptive DashboardPage. The role-specific
      // sidebar links all resolve to it (it adapts by user_type).
      {
        path: 'dashboard',
        component: DashboardShellComponent,
        children: [
          { path: '', loadComponent: () => import('./features/dashboard/pages/dashboard.page').then((m) => m.DashboardPage) },
          { path: 'client', loadComponent: () => import('./features/dashboard/pages/client-dashboard.page').then((m) => m.ClientDashboardPage) },
          { path: 'admin', loadComponent: () => import('./features/dashboard/pages/admin-dashboard.page').then((m) => m.AdminDashboardPage) },
          { path: 'service', loadComponent: () => import('./features/dashboard/pages/service-dashboard.page').then((m) => m.ServiceDashboardPage) },
          { path: 'vendor', loadComponent: () => import('./features/dashboard/pages/vendor-dashboard.page').then((m) => m.VendorDashboardPage) },
          { path: 'system', loadComponent: () => import('./features/dashboard/pages/dashboard.page').then((m) => m.DashboardPage) },
        ],
      },

      // Profile — the real, API-wired profile feature.
      {
        path: 'profile',
        loadChildren: () => import('./features/profile/profile.routes').then((m) => m.profileRoutes),
      },

      // Files (Loans) — placeholder scaffolds (Daxa-styled) pending real pages.
      {
        path: 'files',
        component: FilesShellComponent,
        children: [
          { path: '', component: PlaceholderComponent, data: { title: 'Search / Browse Files', section: 'Files' } },
          { path: 'create', component: PlaceholderComponent, data: { title: 'Create New File', section: 'Files' } },
          { path: 'batches', component: PlaceholderComponent, data: { title: 'Batch Files', section: 'Files' } },
          { path: 'import', component: PlaceholderComponent, data: { title: 'Import File', section: 'Files' } },
        ],
      },

      // Orders
      {
        path: 'orders',
        component: OrdersShellComponent,
        children: [
          { path: '', loadComponent: () => import('./features/orders/pages/order-search.page').then((m) => m.OrderSearchPage) },
          { path: 'wizard', loadComponent: () => import('./features/orders/pages/place-order.page').then((m) => m.PlaceOrderPage) },
          // Wizard pre-filled from a loan (All Products shortcut) — loanId only, no product preselected.
          { path: 'wizard/:loanId', loadComponent: () => import('./features/orders/pages/place-order.page').then((m) => m.PlaceOrderPage) },
          { path: 'wizard/:loanId/:productId', loadComponent: () => import('./features/orders/pages/place-order.page').then((m) => m.PlaceOrderPage) },
          { path: 'jobs', component: PlaceholderComponent, data: { title: 'Batch Ordering', section: 'Orders' } },
          { path: 'notes', component: PlaceholderComponent, data: { title: 'Notes History', section: 'Orders' } },
          // Legacy CakePHP URL — /orders/view/16369 keeps working for old bookmarks.
          { path: 'view/:id', redirectTo: ':id', pathMatch: 'full' },
          // Order detail. MUST stay last: ':id' would otherwise swallow 'wizard'/'jobs'/'notes'.
          { path: ':id', loadComponent: () => import('./features/orders/pages/order-detail.page').then((m) => m.OrderDetailPage) },
        ],
      },

      // Vendor Portal — vendor users, Customer Service and System Admin (SLS-1174);
      // everyone else lands on /forbidden, mirroring the API's VendorPortalRequest gate.
      {
        path: 'vendor-portal',
        component: VendorPortalShellComponent,
        canActivate: [roleGuard],
        data: { roles: [...VENDOR_PORTAL_ROLES] },
        children: [
          { path: '', loadComponent: () => import('./features/orders/pages/vendor-order-list.page').then((m) => m.VendorOrderListPage) },
          // Legacy CakePHP URL — /vendor-portal/view/16369 keeps working for old bookmarks.
          { path: 'view/:id', redirectTo: ':id', pathMatch: 'full' },
          // Vendor order page. MUST stay last: ':id' would otherwise swallow 'view'.
          { path: ':id', loadComponent: () => import('./features/orders/pages/vendor-order.page').then((m) => m.VendorOrderPage) },
        ],
      },

      // Customer Service
      {
        path: 'customer-service',
        component: CustomerServiceShellComponent,
        children: [
          { path: '', component: PlaceholderComponent, data: { title: 'Open Issues', section: 'Customer Service' } },
          { path: 'notes', component: PlaceholderComponent, data: { title: 'Notes History', section: 'Customer Service' } },
          { path: 'chat', component: PlaceholderComponent, data: { title: 'Live Chat', section: 'Customer Service' } },
        ],
      },

      // Reports
      {
        path: 'reports',
        component: ReportsShellComponent,
        children: [
          { path: '', component: PlaceholderComponent, data: { title: 'Reports Dashboard', section: 'Reports' } },
          { path: 'users', component: PlaceholderComponent, data: { title: 'Users Report', section: 'Reports' } },
          { path: 'order-by-month', component: PlaceholderComponent, data: { title: 'Orders by Month', section: 'Reports' } },
          { path: 'order-by-status', component: PlaceholderComponent, data: { title: 'Orders by Status', section: 'Reports' } },
          { path: 'transactions', component: PlaceholderComponent, data: { title: 'Transactions', section: 'Reports' } },
          { path: 'executive-summary', component: PlaceholderComponent, data: { title: 'Executive Summary', section: 'Reports' } },
          { path: 'invoices', component: PlaceholderComponent, data: { title: 'Invoice Report', section: 'Reports' } },
          { path: 'system-messages', component: PlaceholderComponent, data: { title: 'System Messages', section: 'Reports' } },
        ],
      },

      // Admin
      {
        path: 'admin',
        component: AdminShellComponent,
        children: [
          { path: '', component: PlaceholderComponent, data: { title: 'Admin', section: 'Admin' } },
          { path: 'clients', component: PlaceholderComponent, data: { title: 'Clients', section: 'Admin' } },
          { path: 'users', loadComponent: () => import('./features/admin/pages/users-list.page').then((m) => m.UsersListPage) },
          { path: 'vendors', component: PlaceholderComponent, data: { title: 'Vendors', section: 'Admin' } },
          { path: 'products', component: PlaceholderComponent, data: { title: 'Products', section: 'Admin' } },
          // Questionnaires (SLS-1317): System Admin only, mirroring the API's AdminQuestionnaireRequest gate.
          {
            path: 'questionnaires',
            canActivate: [roleGuard],
            data: { roles: ['system-admin'] },
            children: [
              { path: '', loadComponent: () => import('./features/questionnaires/pages/questionnaire-list.page').then((m) => m.QuestionnaireListPage) },
              { path: ':id', loadComponent: () => import('./features/questionnaires/pages/questionnaire-grid.page').then((m) => m.QuestionnaireGridPage) },
              { path: ':id/pages/:pageId', loadComponent: () => import('./features/questionnaires/pages/questionnaire-page-editor.page').then((m) => m.QuestionnairePageEditorPage) },
            ],
          },
        ],
      },

      // Legacy CakePHP URL redirects (bookmark preservation)
      { path: 'loans', redirectTo: 'files', pathMatch: 'full' },
      { path: 'loans/create', redirectTo: 'files/create', pathMatch: 'full' },
      { path: 'batches', redirectTo: 'files/batches', pathMatch: 'full' },
      { path: 'profiles', redirectTo: 'profile', pathMatch: 'full' },
      { path: 'questionnaires/edit/:id', redirectTo: 'admin/questionnaires/:id', pathMatch: 'full' },
      { path: 'questionnaires', redirectTo: 'admin/questionnaires', pathMatch: 'full' },
      { path: 'admin/clients/browse', redirectTo: 'admin/clients', pathMatch: 'full' },
    ],
  },
  {
    path: 'forbidden',
    loadComponent: () => import('./shared/components/forbidden.component').then((m) => m.ForbiddenComponent),
  },
  { path: '**', redirectTo: 'dashboard' },
];
